import React, { useState } from "react";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../../../Components/FormField";
import { muiTheme } from "../../../global/styles/muiTheme";
import { UserProfile } from "../../../global/types/user";
import { Button } from "../../../Components/ui/buttons/button";
import { Alert } from "../../../Components/ui/shadcn/alert";
import { Loader2 } from "lucide-react";

interface PersonalInfoFormProps {
  user: UserProfile | null;
  onUpdate?: (updatedUser: Partial<UserProfile>) => Promise<void>;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required("Nome é obrigatório"),
  lastName: Yup.string().required("Sobrenome é obrigatório"),
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
});

export default function PersonalInfoForm({
  user,
  onUpdate,
}: PersonalInfoFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!user) {
    return (
      <Alert variant="warning">Informações do usuário não disponíveis</Alert>
    );
  }

  const initialValues = {
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    username: user.username || "",
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      setSuccessMessage(null);
      setErrorMessage(null);

      if (onUpdate) {
        await onUpdate({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        });

        setSuccessMessage("Informações atualizadas com sucesso!");
        setIsEditing(false);
      }
    } catch (error: any) {
      setErrorMessage(
        error.message || "Erro ao atualizar informações. Tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {successMessage && (
        <Alert
          variant="success"
          className="mb-2"
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert
          variant="error"
          className="mb-2"
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, resetForm, handleSubmit: formikHandleSubmit }) => (
          <Form onSubmit={(e) => e.preventDefault()}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: muiTheme.spacing(4),
              }}
            >
              <FormField
                name="username"
                label="Nome de usuário"
                disabled
                helperText="Nome de usuário não pode ser alterado"
              />

              <FormField name="firstName" label="Nome" disabled={!isEditing} />

              <FormField
                name="lastName"
                label="Sobrenome"
                disabled={!isEditing}
              />

              <FormField
                name="email"
                label="Email"
                type="email"
                disabled
                helperText="Email de usuário não pode ser alterado"
              />

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                {!isEditing ? (
                  <Button variant="default" onClick={() => setIsEditing(true)}>
                    Editar
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => formikHandleSubmit()}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <Loader2 size={20} /> : null}
                      {isSubmitting ? "Salvando..." : "Salvar"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        resetForm();
                        setSuccessMessage(null);
                        setErrorMessage(null);
                      }}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
